import type { ReactNode } from 'react';

import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Button } from '#/components/Button/Button';
import { Icon } from '#/components/Icon/Icon';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { getI18n } from '#/i18n/getI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { homePath } from '#/routing/routes';
import { dynamic } from '#/utils/dynamic';

import { LayoutHeaderButton } from './Button/LayoutHeaderButton';
import { ApiAndIntegrationsDropdown } from './dropdowns/api-and-integrations/ApiAndIntegrationsDropdown';
import { FindAJobDropdown } from './dropdowns/find-a-job/FindAJobDropdown';
import { ParticipateDropdown } from './dropdowns/participate/ParticipateDropdown';
import { PostOnIdealistDropdown } from './dropdowns/post-on-idealist/PostOnIdealistDropdown';
import { VolunteerDropdown } from './dropdowns/volunteer/VolunteerDropdown';
import { HamburgerMenu } from './HamburgerMenu/HamburgerMenu';
import {
  LayoutFocusedHeaderSubmenuHolder,
  LayoutHeaderApiAndIntegrationsDropdownHolder,
  LayoutHeaderAuthHolder,
  LayoutHeaderAuthLink,
  LayoutHeaderDesktopDonateLinkHolder,
  LayoutHeaderFocusedTitle,
  LayoutHeaderHolder,
  LayoutHeaderLeft,
  LayoutHeaderLogoHolder,
  LayoutHeaderLogoLink,
  LayoutHeaderProgressBarHolder,
  LayoutHeaderRight,
  LayoutHeaderSignupLinkHolder,
  LayoutHeaderTopSectionHolder,
} from './LayoutHeader.styled';
import { IDEALIST_DAYS_PATH } from './layoutHeaderConstants';
import { LayoutHeaderLinks } from './LayoutHeaderLinks';
import { LayoutHeaderMobileSearchIcon } from './LayoutHeaderMobileSearchIcon';
import { LayoutHeaderMobileSearchMenu } from './LayoutHeaderMobileSearchMenu';
import { LayoutHeaderSearchSubmenu } from './SearchSubmenu/LayoutHeaderSearchSubmenu';
import { WorkspaceDropdown } from './UserItems/dropdowns/workspace/WorkspaceDropdown';

const DynamicLayoutHeaderPtSunsetBanner = dynamic(
  () =>
    import('./PtSunsetBanner/LayoutHeaderPtSunsetBanner').then((m) => ({
      default: m.LayoutHeaderPtSunsetBanner,
    })),
  { strategy: 'always' },
);

const DynamicLayoutHeaderUserItems = dynamic(
  () =>
    import('./UserItems/LayoutHeaderUserItems').then((m) => ({
      default: m.LayoutHeaderUserItems,
    })),
  { strategy: 'always' },
);

const DONATE_BUTTON_DETAILS = {
  en: {
    text: 'Donate',
    href: 'https://giving.classy.org/campaign/673245/donate',
  },
  es: {
    text: 'Haz una Donación',
    href: '/es/dona',
  },
} as const satisfies Record<I18nLocale, { text: string; href: string }>;

type Props = {
  variant: 'default' | 'focused';
  defaultSearchType: SearchType;
  searchFromPageType: SearchFromPageType | undefined;
  showDesktopSearch: boolean;
  isSearchLandingPage: boolean;
  showSearchRefinements: boolean;
  searchRefinements: ReactNode;
  isSearchPage: boolean;
  hideMobileHeaderOnScroll: boolean;
  mobileSearchIcon: boolean;
  user: ApiUser | null;
  locale: I18nLocale;
  redirectTo: string;
  hidePtSunsetBanner: boolean;
  focused: {
    showSubmenu: boolean;
    showCart: boolean;
    submenu: ReactNode;
    title: string;
    exitLink: string;
  };
};

export function LayoutHeader({
  variant,
  defaultSearchType,
  searchFromPageType,
  showDesktopSearch,
  isSearchLandingPage,
  showSearchRefinements,
  searchRefinements,
  isSearchPage,
  hideMobileHeaderOnScroll,
  mobileSearchIcon,
  user,
  locale,
  redirectTo,
  hidePtSunsetBanner,
  focused,
}: Props) {
  const { i18n } = getI18n(locale);
  const isLogoCollapsible = variant === 'default' && mobileSearchIcon;
  const showPtSunsetBanner =
    variant === 'default' &&
    user?.migratedFromPt === true &&
    !hidePtSunsetBanner;
  return (
    <LayoutHeaderHolder
      id="layout-header"
      data-qa-id="page-header"
      data-focused={variant === 'focused' ? true : undefined}
    >
      {showPtSunsetBanner && <DynamicLayoutHeaderPtSunsetBanner />}

      <LayoutHeaderTopSectionHolder>
        <LayoutHeaderLeft data-collapsible={isLogoCollapsible || undefined}>
          <LayoutHeaderLogoHolder>
            <LayoutHeaderLogoLink
              type="link"
              href={homePath.with({ lang: locale })}
              title="Idealist"
              clickActions={{ track: { name: 'Idealist Logo Header' } }}
              qaId="header-logo-link"
            >
              <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
            </LayoutHeaderLogoLink>
          </LayoutHeaderLogoHolder>

          {variant === 'default' && (
            <LayoutHeaderLinks
              hideMobileHeaderOnScroll={hideMobileHeaderOnScroll}
            >
              {locale === I18nLocale.ES ? (
                <ParticipateDropdown locale={locale} user={user} />
              ) : (
                <LayoutHeaderButton
                  variant="grey"
                  data={{
                    type: 'link',
                    href: IDEALIST_DAYS_PATH[locale],
                    clickActions: {
                      track: {
                        name: 'Idealist Days',
                        properties: { context: 'Header' },
                      },
                    },
                  }}
                >
                  Get Involved
                </LayoutHeaderButton>
              )}

              <FindAJobDropdown locale={locale} />

              <VolunteerDropdown locale={locale} />

              <PostOnIdealistDropdown locale={locale} user={user} />

              {locale === I18nLocale.EN && (
                <LayoutHeaderApiAndIntegrationsDropdownHolder>
                  <ApiAndIntegrationsDropdown />
                </LayoutHeaderApiAndIntegrationsDropdownHolder>
              )}

              {user?.orgs.length === 0 && (
                <LayoutHeaderDesktopDonateLinkHolder>
                  <LayoutHeaderButton
                    variant="grey"
                    data={{
                      type: 'link',
                      href: DONATE_BUTTON_DETAILS[locale].href,
                      clickActions: {
                        track: {
                          name: 'Donate',
                          properties: { context: 'Header' },
                        },
                      },
                    }}
                  >
                    {DONATE_BUTTON_DETAILS[locale].text}
                  </LayoutHeaderButton>
                </LayoutHeaderDesktopDonateLinkHolder>
              )}
            </LayoutHeaderLinks>
          )}
        </LayoutHeaderLeft>

        {variant === 'focused' && (
          <LayoutHeaderFocusedTitle>{focused.title}</LayoutHeaderFocusedTitle>
        )}

        <LayoutHeaderRight>
          {variant === 'default' && mobileSearchIcon && (
            <LayoutHeaderMobileSearchIcon
              defaultSearchType={defaultSearchType}
              searchFromPageType={searchFromPageType}
              isSearchLandingPage={isSearchLandingPage}
              isSearchPage={isSearchPage}
            />
          )}

          {!user && variant === 'default' && (
            <LayoutHeaderAuthHolder>
              <LayoutHeaderAuthLink
                type="button"
                clickActions={{
                  track: { name: 'Log In Header' },
                  modal: {
                    type: 'LOGIN',
                    props: { redirectTo, manualLogin: true },
                  },
                }}
                qaId="login-link"
              >
                {i18n('Log In')}
              </LayoutHeaderAuthLink>

              <LayoutHeaderSignupLinkHolder>
                <LayoutHeaderAuthLink
                  type="button"
                  clickActions={{
                    track: { name: 'Signup Header' },
                    modal: { type: 'SIGNUP', props: {} },
                  }}
                  qaId="signup-link"
                >
                  {i18n('Sign Up')}
                </LayoutHeaderAuthLink>
              </LayoutHeaderSignupLinkHolder>
            </LayoutHeaderAuthHolder>
          )}

          {variant === 'default' && user && user.orgs.length > 0 && (
            <div>
              <WorkspaceDropdown user={user} />
            </div>
          )}

          {variant === 'default' && (
            <LayoutHeaderMobileSearchMenu
              trigger={
                <LayoutHeaderButton
                  data={{ type: 'inert' }}
                  ariaLabel={i18n('Search')}
                  qaId="search-menu-button"
                  variant="grey"
                  smallHorizontalPaddingOnMobile
                >
                  <Icon name="search" size={24} />
                </LayoutHeaderButton>
              }
              defaultSearchType={defaultSearchType}
              searchFromPageType={searchFromPageType}
              isSearchLandingPage={isSearchLandingPage}
              isSearchPage={isSearchPage}
            />
          )}

          {user && (variant === 'default' || focused.showCart) && (
            <DynamicLayoutHeaderUserItems variant={variant} />
          )}

          {variant === 'default' && <HamburgerMenu locale={locale} />}

          {variant === 'focused' && (
            <div>
              <Button
                type="link"
                variant="tertiary-grey"
                href={focused.exitLink}
                icon={{ name: 'close' }}
              />
            </div>
          )}
        </LayoutHeaderRight>
      </LayoutHeaderTopSectionHolder>

      {variant === 'default' && (
        <LayoutHeaderSearchSubmenu
          defaultSearchType={defaultSearchType}
          searchFromPageType={searchFromPageType}
          showDesktopSearch={showDesktopSearch}
          isSearchLandingPage={isSearchLandingPage}
          showSearchRefinements={showSearchRefinements}
          searchRefinements={searchRefinements}
          isSearchPage={isSearchPage}
          hideMobileHeaderOnScroll={hideMobileHeaderOnScroll}
          mobileSearchIcon={mobileSearchIcon}
        />
      )}

      {variant === 'focused' && focused.showSubmenu && (
        <LayoutFocusedHeaderSubmenuHolder id="layout-focused-header-submenu">
          {focused.submenu}
        </LayoutFocusedHeaderSubmenuHolder>
      )}

      <LayoutHeaderProgressBarHolder>
        <RouteLoadingProgressBar />
      </LayoutHeaderProgressBarHolder>
    </LayoutHeaderHolder>
  );
}
